/** Client ID of the multi-tenant Entra app registration (public, not a secret). */
export const CLIENT_ID: string = (import.meta.env.VITE_CLIENT_ID as string | undefined) ?? '';

/**
 * Who can sign in: 'organizations' (any work or school account) for production; the test
 * environment's single-tenant registration sets its tenant ID (VITE_TENANT) instead.
 */
export const SIGN_IN_TENANT: string = (import.meta.env.VITE_TENANT as string | undefined) || 'organizations';

/** Shown in the page title and top bar so the test environment can't be mistaken for production. */
export const ENVIRONMENT_LABEL: string = (import.meta.env.VITE_ENVIRONMENT_LABEL as string | undefined) ?? '';

const graph = (scope: string): string => `https://graph.microsoft.com/${scope}`;

/**
 * Asked for at sign-in: enough to scan, plan and apply. Delegated, so the app can only see
 * and change what the signed-in user can.
 */
export const BASE_SCOPES: string[] = ['User.Read', 'Sites.Read.All', 'Files.ReadWrite.All', 'Channel.ReadBasic.All'].map(graph);

/**
 * Asked for only the first time someone applies or undoes changes: creating and writing the
 * audit list. People who only scan and review never see this request.
 */
export const MANAGE_SCOPES: string[] = [graph('Sites.Manage.All')];

/**
 * Asked for only when an admin presses "Create retention labels" (law firms whose Microsoft 365
 * plan includes them). Delegated: the signed-in person must be allowed to manage retention labels.
 */
export const LABEL_SCOPES: string[] = [graph('RecordsManagement.ReadWrite.All')];

/**
 * Asked for the first time someone opens a request to a client as an Outlook draft: creating a
 * draft in their own mailbox (they read it and send it themselves; nothing is sent for them).
 */
export const MAIL_SCOPES: string[] = [graph('Mail.ReadWrite')];

/** Asked for the first time someone adds a deadline to their Outlook calendar. */
export const CALENDAR_SCOPES: string[] = [graph('Calendars.ReadWrite')];

/** Our own backend (AI features and credits). */
export const API_SCOPES: string[] = [`api://${CLIENT_ID}/access_as_user`];

/**
 * Where the AI calls go: our own Functions app (no short request limit) when set, e.g.
 * https://func-aiadmin-test.azurewebsites.net/api; otherwise the site's built-in API.
 */
export const AI_API_BASE: string = ((import.meta.env.VITE_AI_API_BASE as string | undefined) ?? '').replace(/\/+$/, '');
